import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service, FarmingBidHistoryEntry, FarmingBidHistoryData } from '@/services/pmt2Service';
import { Timestamp } from 'firebase/firestore';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'; // Using Card for sections

interface FarmingBidHistoryDashboardProps {
  product: PMT2Product | null;
}

// Define the order and titles for the sections
const BID_SECTION_ORDER: string[] = [
  'Farming',
  'Broad',
  'CTA',
  'Full Auto',
  'Full Auto Close-Match',
  'Full Auto Loose-Match',
  'Full Auto Substitutes',
  'Full Auto Complements',
];

/**
 * Farming Bid History Dashboard Component
 * Displays the history of various farming-related bid overrides for a selected product.
 */
const FarmingBidHistoryDashboard: React.FC<FarmingBidHistoryDashboardProps> = ({ product }) => {
  const [historyData, setHistoryData] = useState<FarmingBidHistoryData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHistoryData = async () => {
      if (!product) {
        setHistoryData({});
        setError(null);
        return setLoading(false);
      }

      try {
        setLoading(true);
        console.log(`[FarmingBidHistoryDashboard] Fetching data for ${product.companyName}/${product.id}`);
        const data = await pmt2Service.getFarmingBidHistoryData(product.companyName, product.id);
        console.log('[FarmingBidHistoryDashboard] Data received:', data);
        setHistoryData(data);
        setError(null);
      } catch (err) {
        console.error('[FarmingBidHistoryDashboard] Error fetching data:', err instanceof Error ? err.message : err);
        setError('Failed to load farming bid history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchHistoryData();
  }, [product]);

  // --- Helper Functions (Adapted from PPCStatusHistoryDashboard) ---

  const sortEntries = (entries: FarmingBidHistoryEntry[]): FarmingBidHistoryEntry[] => {
    return [...entries].sort((a, b) => {
      if (a.date?.toDate && b.date?.toDate) {
        return b.date.toDate().getTime() - a.date.toDate().getTime();
      } else if (a.Date && b.Date) {
        try {
          // Attempt to parse MM/DD/YY or MM/DD/YYYY
          const parseDateString = (dateStr: string): Date => {
            const parts = dateStr.split('/');
            if (parts.length !== 3) throw new Error('Invalid date format');
            const month = parseInt(parts[0], 10) - 1;
            const day = parseInt(parts[1], 10);
            let year = parseInt(parts[2], 10);
            if (year < 100) year += 2000;
            return new Date(year, month, day);
          };
          const dateA = parseDateString(a.Date);
          const dateB = parseDateString(b.Date);
          return dateB.getTime() - dateA.getTime();
        } catch {
          // Fallback if parsing fails
          return 0;
        }
      }
      return 0; // Fallback if no valid date info
    });
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'Unknown date';
    try {
      const parts = dateStr.split('/');
      if (parts.length !== 3) return dateStr;
      const month = parseInt(parts[0], 10) - 1;
      const day = parseInt(parts[1], 10);
      let year = parseInt(parts[2], 10);
      if (year < 100) year += 2000;
      const date = new Date(year, month, day);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); // Using short month
    } catch {
      return dateStr;
    }
  };

  // Helper function to format currency
  const formatCurrency = (value: any): string => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return typeof value === 'string' && value.trim() !== '' ? value : 'N/A'; // Return original string if not empty, else N/A
    }
    return `$${numericValue.toFixed(2)}`;
  };

  // --- Rendering Logic ---

  if (!product) return null; // Don't render anything if no product is selected

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Farming Bid History</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading history...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Farming Bid History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if there's any data at all across all sections
  const hasAnyData = BID_SECTION_ORDER.some(title => historyData[title]?.length > 0);

  if (!hasAnyData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Farming Bid History</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 dark:text-gray-400">No farming bid history available for this product.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Farming Bid History</h2>
      {/* Grid container for two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {BID_SECTION_ORDER.map(title => {
          const entries = historyData[title];
          if (!entries || entries.length === 0) {
            return null; // Don't render section if no entries
          }

          const sorted = sortEntries(entries);

          return (
            <Card key={title}>
              <CardHeader>
                <CardTitle>{title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px]">Date</TableHead>
                        <TableHead className="w-[150px]">Your Name</TableHead>
                        <TableHead className="w-[100px]">New Value</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sorted.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{formatDate(entry.Date)}</TableCell>
                          <TableCell>{entry['Your Name'] || 'N/A'}</TableCell>
                          <TableCell>{formatCurrency(entry['New Value'])}</TableCell>
                          <TableCell className="whitespace-pre-wrap">{entry.note || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div> {/* Close grid container */}
    </div>
  );
};

export default FarmingBidHistoryDashboard;